<template lang="pug">
    v-col(cols='12' md='12')
        v-alert(
            border="left"
            colored-border
            elevation="2"
            :color="isWaitingPhoneCall ? 'red lighten-2' : 'green lighten-2'"
            style="padding-top: 0; padding-bottom: 0; margin-bottom: 8px;"
        )
            v-container
                v-row(align='center')
                    v-col.grow
                        .div(v-if="isWaitingPhoneCall")
                            RunningClock(
                                :before-text="longestWaitingText"
                                :timestamp="longestHoldStartTime"
                            )
                        .div(v-else)
                            | 0 calls waiting
                    v-col.shrink
                        v-dialog(v-model='dialog' fullscreen hide-overlay transition='dialog-bottom-transition')
                            template(v-slot:activator='{ on, attrs }')                      
                                v-btn(color='var(--volie-gray)' dark v-bind='attrs' v-on='on' fab x-small ripple)
                                    v-icon mdi-format-list-bulleted-square

                            v-card
                                v-toolbar(dark color='primary')
                                    v-toolbar-title Calls Waiting
                                    v-spacer
                                    v-btn(icon dark @click='dialog = false')
                                        v-icon mdi-close

                                v-card-text
                                    v-simple-table(v-if="isWaitingPhoneCall")
                                        template(v-slot:default)
                                            thead(style="padding-top=15px;")
                                                tr
                                                    th
                                                    th
                                                    th Hold Time
                                                    th Organization
                                                    th Queue
                                                    th Language
                                                    th Caller ID
                                                    th Customer
                                            tbody
                                                tr.white--text(
                                                    v-for='(ccq, idx) in callsWaiting'
                                                    :key='ccq.call_communication_queue_id'
                                                    v-bind:style="{ backgroundColor: rowColor(ccq) }"
                                                )
                                                    td.no-wrap.xs-xs-width {{ idx + 1 | number }}
                                                    td.no-wrap.xs-xs-width 
                                                        v-icon.ml-1.text-white 
                                                            template(v-if="ccq.status === 'ivr_callback'") mdi-phone-outgoing
                                                            template(v-else) mdi-phone-incoming
                                                    td.no-wrap.sm-width
                                                        template(v-if="(ccq.hold_time_in_seconds || 0) < 600")
                                                            strong {{ (ccq.hold_time_in_seconds || 0) | secondsToFullWords }}
                                                        Timeago(
                                                            v-else
                                                            :datetime="ccq.start_time"
                                                            :auto-update="60"
                                                        )
                                                    td.no-wrap.sm-width
                                                        v-tooltip(top)
                                                            template(v-slot:activator='{ on, attrs }')
                                                                span(v-bind="attrs" v-on="on" role="text")
                                                                    | {{ ccq.organization_name | str_limit(35) }}
                                                            span
                                                                | {{ ccq.organization_name }}
                                                    td.no-wrap.sm-width
                                                        v-tooltip(top)
                                                            template(v-slot:activator='{ on, attrs }')
                                                                span(v-bind="attrs" v-on="on" role="text")
                                                                    | {{ ccq.communication_queue_name | str_limit(35) }}
                                                            span
                                                                | {{ ccq.communication_queue_name }}
                                                    td.no-wrap.sm-width {{ ccq.language_name }}
                                                    td.no-wrap.sm-width {{ ccq.customer_phone_number | tel }}
                                                    td.no-wrap.sm-width {{ customerName(ccq) }}

                                    v-alert.mx-lg-auto.mt-md-4.mb-md-4(type="success" v-else)
                                        | There are not currently any calls waiting

</template>

<script lang="ts">
    import Vue from 'vue';

    // Components
    import RunningClock from '@/components/shared/running_clock.vue';

    export default Vue.extend({
        data() {
            return {
                dialog: false,
            }
        },
        methods: {
            customerName(ccq) {

                // Verify
                if (!ccq) {
                    return;
                }

                // Init
                const pieces = [];

                // Lead check
                if (ccq.is_outbound_lead === true) {
                    pieces.push("(LEAD)")
                }

                // Customer
                if (ccq.customer_id > 0) {
                    pieces.push(ccq.customer_full_name);
                } else if (ccq.customer_match_code === 2) {
                    pieces.push("Multiple potentials found");
                } else if (ccq.customer_match_code === 3) {
                    pieces.push("No Customer Matches");
                } else {
                    pieces.push("Unknown");
                }

                return pieces.join(" ");
            },
            rowColor(ccq) {

                // Init
                const seconds = (ccq?.hold_time_in_seconds || 0);

                // alert error
                if (seconds >= 90) {
                    return "var(--error)";
                }

                // alert warning
                if (seconds >= 30) {
                    return "var(--orange)";
                }

                // alert success
                return "var(--success)";
            },
        },
        computed: {
            isWaitingPhoneCall() {
                return this.callsWaiting?.length > 0;
            },
            longestHoldStartTime() {
                
                // Verify that we have valid campaign customers
                if (!this.isWaitingPhoneCall) {
                    return null;
                }

                // Init
                let oldestAt = null;
                let oldestID = null;

                // Loop through records and find the longest waiting time if applicable
                for (let i = 0; i < this.callsWaiting.length; i++) {
                    const ccq = this.callsWaiting[i];

                    // Verify campaign customer has valid unresponded to incoming message
                    if (!(ccq?.call_communication_queue_id > 0 && !!ccq?.start_time)) {
                        continue;
                    }

                    // If the oldest is null or the current is less than the oldest
                    if (oldestID === null || ccq?.call_communication_queue_id < oldestID) {

                        // Set mins
                        oldestAt = ccq?.start_time;
                        oldestID = ccq?.call_communication_queue_id;
                    }
                }

                return oldestAt;
            },
            longestWaitingText() {
                return "(" + this.totalCallsWaiting + ") Longest Waiting:";  
            },
            totalCallsWaiting() {
                return (this.callsWaiting?.length || 0);
            },
        },
        components: {
            RunningClock,
        },
        props: {
            callsWaiting: Array,
        },
    });
</script>

<style lang="scss" scoped>
    #app {
        .list-item-icon-margin-override {
            margin: 8px 0 8px 16px;
        }

        .dense-subheader {
            height: 24px;
        }
    }
</style>
